import { useQuery } from '@sequencehq/api'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import {
  Text,
  CustomFieldsEditor,
  GreyGrey80,
  IconCircleInfo,
  IndigoIndigo60,
  InputSelectControl,
  InputSelectControlField,
  Link,
  Primary13SemiBold,
  Primary14SemiBold,
  TagInputSelectControl,
  TagInputSelectControlField,
  TextAreaField,
  TextInputField,
  TooltipLegacy,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useForm, usePrevious } from '@sequencehq/utils'
import {
  required,
  requiredIfFieldHasValue,
  requiredIfFieldPopulated
} from '@sequencehq/utils/validation'
import { match } from 'ts-pattern'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { isEmpty } from 'lodash'
import { UsageEventTypeSelect } from 'UsageEvents/index'
import { dequal } from 'dequal'

export type UsageMetricInput = Omit<
  DashboardApi20240730.PostUsageMetric.PostUsageMetricBody,
  'parameters' | 'caseSensitive'
>

type UsageMetricFormValues = {
  name: string
  description: string
  eventType: string
  aggregationType: 'COUNT' | 'UNIQUE' | 'SUM'
  aggregationProperty: string
  eventFilterFields: {
    id: string
    eventProperty: string
    operator: string
    value: string
  }[]
}

const eventFilterOperatorOptions = [
  {
    label: 'matches',
    value: 'MATCHES'
  },
  {
    label: 'does not match',
    value: 'DOES_NOT_MATCH'
  }
]

interface Props {
  initialValues: UsageMetricInput | null
  onChange: (values: UsageMetricInput) => void
  onValidationChange: (isValid: boolean) => void
  showValidationErrors: boolean
}

const apiToFormAdapter = {
  toForm: (apiData: UsageMetricInput | null): UsageMetricFormValues => {
    if (!apiData) {
      return {
        name: '',
        description: '',
        eventType: '',
        aggregationType: 'COUNT',
        aggregationProperty: '',
        eventFilterFields: [
          {
            id: crypto.randomUUID(),
            eventProperty: '',
            operator: 'MATCHES',
            value: ''
          }
        ]
      }
    }

    const eventFilterEntries = Object.entries(apiData.propertyFilters ?? {})
    const eventFilterFields = eventFilterEntries.length
      ? eventFilterEntries.map(([key, values]) => ({
          id: crypto.randomUUID(),
          eventProperty: key,
          operator: apiData.propertiesToNegate?.includes(key)
            ? 'DOES_NOT_MATCH'
            : 'MATCHES',
          value: (values as string[]).join(', ')
        }))
      : [
          {
            id: crypto.randomUUID(),
            eventProperty: '',
            operator: 'MATCHES',
            value: ''
          }
        ]

    return {
      name: apiData.name,
      description: apiData.description ?? '',
      eventType: apiData.eventType,
      aggregationType:
        apiData.aggregationType === 'CUSTOM'
          ? 'COUNT'
          : apiData.aggregationType,
      aggregationProperty: apiData.aggregationProperty ?? '',
      eventFilterFields
    }
  },
  toApi: (formData: UsageMetricFormValues): UsageMetricInput => {
    const propertyFilters = formData.eventFilterFields.reduce(
      (acc, field) => {
        if (!field.eventProperty) {
          return acc
        }

        const trimmedValues = field.value
          .split(',')
          .map(v => v.trim())
          .filter(v => v !== '')
        if (trimmedValues.length === 0) {
          return acc
        }

        return {
          ...acc,
          [field.eventProperty]: trimmedValues
        }
      },
      {} as Record<string, string[]>
    )

    const propertiesToNegate = formData.eventFilterFields
      .filter(({ operator }) => operator === 'DOES_NOT_MATCH')
      .map(({ eventProperty }) => eventProperty)

    return {
      name: formData.name,
      description: formData.description,
      eventType: formData.eventType,
      aggregationType: formData.aggregationType,
      aggregationProperty: formData.aggregationProperty
        ? formData.aggregationProperty
        : undefined,
      propertyFilters: isEmpty(propertyFilters) ? undefined : propertyFilters,
      propertiesToNegate: isEmpty(propertiesToNegate)
        ? undefined
        : propertiesToNegate,
      metricType: 'SIMPLE'
    }
  }
}

export const UsageMetricForm = (props: Props) => {
  const { data: usageEventPropertyData } = useQuery(
    dashboard20240730Client.getUniqueUsageEventProperties
  )

  const usageEventPropertyOptions =
    usageEventPropertyData?.items.map(v => ({
      id: v,
      value: v,
      label: v
    })) ?? []

  const initialData = useMemo(() => {
    return apiToFormAdapter.toForm(props.initialValues)
  }, [props.initialValues])

  const [eventFilterFields, setEventFilterFields] = useState<
    {
      id: string
      eventProperty: string
      operator: string
      value: string
    }[]
  >(initialData.eventFilterFields)

  const form = useForm<UsageMetricFormValues>({
    value: initialData,
    reinitializeOnValueChange: false,
    showValidationErrors: props.showValidationErrors,
    fieldConfiguration: [
      {
        property: 'name',
        validation: [required]
      },
      {
        property: 'description'
      },
      {
        property: 'eventType',
        validation: [required]
      },
      {
        property: 'aggregationType',
        validation: [required]
      },
      {
        property: 'aggregationProperty',
        validation: [
          requiredIfFieldHasValue({
            otherField: 'aggregationType',
            values: ['SUM', 'UNIQUE']
          })
        ]
      }
    ],
    onValidationStateChange: isValid => props.onValidationChange(isValid)
  })

  const previousFormData = usePrevious(form.queries.formData)
  const previousEventFilterFields = usePrevious(eventFilterFields)

  useEffect(() => {
    if (
      dequal(previousFormData, form.queries.formData) &&
      dequal(previousEventFilterFields, eventFilterFields)
    ) {
      return
    }

    props.onChange(
      apiToFormAdapter.toApi({
        ...form.queries.formData,
        eventFilterFields
      })
    )
  }, [
    eventFilterFields,
    form.queries.formData,
    previousEventFilterFields,
    previousFormData,
    props
  ])

  return (
    <Flex flexDirection="column" gap="16px">
      <Text>
        Create a metric to aggregate usage events and automate usage-based
        billing. Read more in the guide{' '}
        <Link
          color={IndigoIndigo60}
          to="https://docs.sequencehq.com/metrics-events/usage-metrics"
        >
          here.
        </Link>
      </Text>
      <Section title="Basic information">
        <TextInputField
          data-testid="usageMetric.name"
          tooltip={{
            title: 'Metric name',
            content: 'Give your metric a name that makes it easy to identify.'
          }}
          label="Name"
          placeholder="API Calls"
          value={form.fields.name.value}
          onChange={e => form.fields.name.onChange(e.target.value)}
          validationErrors={form.fields.name.validationErrors}
        />
        <TextAreaField
          data-testid="usageMetric.description"
          label="Description"
          value={form.fields.description.value}
          validationErrors={form.fields.description.validationErrors}
          onChange={form.fields.description.onChange}
          placeholder="Metric used to calculate API Calls event"
        />
      </Section>

      <Section title="Usage">
        <UsageEventTypeSelect
          data-testid="usageMetric.eventType"
          value={form.fields.eventType.value}
          onChange={form.fields.eventType.onChange}
          validationErrors={form.fields.eventType.validationErrors}
        />
        <InputSelectControlField
          data-testid="usageMetric.aggregationType"
          label="Aggregation type"
          placeholder="Select how to combine events"
          tooltip={{
            title: 'Aggregation type',
            content:
              'Select how you want to aggregate events. Sequence supports a simple count of events (e.g. number of API calls), a sum of an event property (e.g. transaction volume) or a count of unique events (e.g. unique user ids).'
          }}
          options={[
            {
              label: 'Count of usage values',
              value: 'COUNT'
            },
            {
              label: 'Sum of usage values',
              value: 'SUM'
            },
            {
              label: 'Unique usage values',
              value: 'UNIQUE'
            }
          ]}
          initialValue={form.fields.aggregationType.value}
          onChange={value =>
            form.fields.aggregationType.onChange(
              value as 'COUNT' | 'UNIQUE' | 'SUM'
            )
          }
        />
        {form.queries.formData.aggregationType &&
        ['SUM', 'UNIQUE'].includes(form.queries.formData.aggregationType) ? (
          <TagInputSelectControlField
            data-testid="usageMetric.aggregationProperty"
            allowCreate
            label="Over event property"
            placeholder="Select event property"
            tooltip={{
              title: form.fields.aggregationType.value,
              content: match(form.fields.aggregationType.value)
                .with('SUM', () => 'Enter the event property to sum up.')
                .with(
                  'UNIQUE',
                  () => 'Enter the event property to count unique events by.'
                )
                .otherwise(() => '')
            }}
            options={usageEventPropertyOptions}
            value={form.fields.aggregationProperty.value}
            onChange={form.fields.aggregationProperty.onChange}
            validationErrors={form.fields.aggregationProperty.validationErrors}
          />
        ) : null}
      </Section>

      <Section title="Filter">
        <Box width="100%">
          <Flex
            alignContent="center"
            alignItems="center"
            gap={2}
            marginBottom="8px"
          >
            <Text
              width="164px"
              {...Primary13SemiBold}
              color={GreyGrey80}
              display="flex"
              gap={1}
            >
              Event property{' '}
              <TooltipLegacy
                title="Filter"
                content={
                  <Flex direction="column">
                    <Flex>Use filters to narrow the scope of events.</Flex>
                    <Box height={2} />
                    <Flex>
                      To filter for multiple values, enter comma-separated
                      items.
                    </Flex>
                    <Box height={2} />
                    <Flex>
                      Only events matching all filters will be included in the
                      metric calculation. Enter comma-separated values to filter
                      for multiple values in a single filter. Each value is
                      treated as an 'OR' condition. Each additional filter acts
                      as an 'AND'.
                    </Flex>
                  </Flex>
                }
              >
                <IconCircleInfo color={GreyGrey80} />
              </TooltipLegacy>
            </Text>
            <Box width="130px" />
            <Text {...Primary13SemiBold} color={GreyGrey80}>
              Value
            </Text>
          </Flex>
          <CustomFieldsEditor
            fields={eventFilterFields}
            onAdd={() =>
              setEventFilterFields([
                ...eventFilterFields,
                {
                  id: crypto.randomUUID(),
                  eventProperty: '',
                  operator: 'MATCHES',
                  value: ''
                }
              ])
            }
            onRemove={id =>
              setEventFilterFields(eventFilterFields.filter(f => f.id !== id))
            }
            addButtonLabel="Add filter"
            addButtonTestId="usageMetric.addFilter"
            addButtonVariant="ghost"
            alwaysShowOneRow={false}
            data-testid="usageMetric.filters"
            renderCells={(field, siblingFields) => {
              const usedKeys = siblingFields.map(s => s.eventProperty)
              const availableOptions = usageEventPropertyOptions.filter(
                uep => !usedKeys.includes(uep.label)
              )

              return (
                <>
                  <Box width="164px">
                    <TagInputSelectControl
                      allowCreate
                      data-testid="usageMetric.filter.eventProperty"
                      unavailableKeys={usedKeys}
                      triggerStyle={{ width: '164px' }}
                      placeholder="Select or add"
                      value={field.eventProperty}
                      onChange={(value: string) =>
                        setEventFilterFields(prev =>
                          prev.map(f =>
                            f.id === field.id
                              ? { ...f, eventProperty: value }
                              : f
                          )
                        )
                      }
                      options={availableOptions}
                    />
                  </Box>
                  <Box width="130px">
                    <InputSelectControl
                      data-testid="usageMetric.filter.operator"
                      initialValue={field.operator}
                      onChange={(value: string) =>
                        setEventFilterFields(prev =>
                          prev.map(f =>
                            f.id === field.id ? { ...f, operator: value } : f
                          )
                        )
                      }
                      triggerStyle={{ width: '130px' }}
                      options={eventFilterOperatorOptions}
                    />
                  </Box>
                  <Box flex="1 1 0" minWidth="0">
                    <TextInputField
                      data-testid="usageMetric.filter.value"
                      width="100%"
                      label=""
                      placeholder="Value"
                      value={field.value}
                      onChange={e =>
                        setEventFilterFields(prev =>
                          prev.map(f =>
                            f.id === field.id
                              ? { ...f, value: e.target.value }
                              : f
                          )
                        )
                      }
                      styles={{
                        wrapper: {
                          marginBottom: 0
                        }
                      }}
                    />
                  </Box>
                </>
              )
            }}
          />
        </Box>
      </Section>
    </Flex>
  )
}

// Once the v1 field variants are gone, replace this local Section with shared
// form-layout components (e.g. `Layout.Form` for spacing, `Layout.Form.Section`
// for sections).
const Section = ({
  title,
  children
}: {
  title: string
  children: ReactNode
}) => (
  <Box width="100%">
    <Text mb="8px" {...Primary14SemiBold}>
      {title}
    </Text>
    <Flex flexDirection="column" gap="16px">
      {children}
    </Flex>
  </Box>
)
